/**
 * Entrar con Google SIN salir de nuestro dominio.
 *
 * ── Por qué ──────────────────────────────────────────────────────────────────
 * El acceso de siempre (`signInWithOAuth`) manda el navegador a Google con la
 * dirección de vuelta de Supabase, así que Google anuncia «Ir a
 * eckuoqipoiqnehgzbcec.supabase.co»: un dominio que no es nuestro, que nadie
 * reconoce y que —por no ser nuestro— no se puede verificar ante Google.
 *
 * Aquí Google pregunta en su propia ventana, devuelve un token de identidad al
 * navegador y ese token se le entrega a Supabase (`signInWithIdToken`). No hay
 * redirección, así que lo que se lee en la pantalla de Google es nuestro
 * dominio, y la verificación de la marca sólo depende de `veltica.com`.
 *
 * ── El nonce ─────────────────────────────────────────────────────────────────
 * Google recibe el nonce YA CIFRADO (SHA-256) y Supabase el original: así
 * Supabase comprueba que el token se pidió para esta sesión y no es uno
 * reutilizado. Por eso «Skip nonce checks» se queda apagado en Supabase.
 *
 * ── Si no se puede ───────────────────────────────────────────────────────────
 * Sin clave configurada, con el guion de Google bloqueado, o si quien entra
 * cierra la ventana sin elegir cuenta, esto devuelve `unavailable` y quien llama
 * cae al acceso con redirección de siempre. Nunca deja a nadie sin poder entrar.
 */
import { supabase } from './supabase'

const CLIENT_ID = (import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined)?.trim() ?? ''
const SCRIPT_URL = 'https://accounts.google.com/gsi/client'
/** Lo que se espera a que alguien elija cuenta antes de ofrecer el otro camino. */
const PROMPT_TIMEOUT_MS = 60_000

type CredentialResponse = { credential?: string }
type GoogleIdentity = {
  accounts: {
    id: {
      initialize: (config: Record<string, unknown>) => void
      prompt: (listener?: (notification: PromptNotification) => void) => void
      cancel: () => void
      disableAutoSelect: () => void
    }
  }
}
type PromptNotification = {
  isNotDisplayed?: () => boolean
  isSkippedMoment?: () => boolean
  isDismissedMoment?: () => boolean
  getNotDisplayedReason?: () => string
  getDismissedReason?: () => string
}

declare global {
  interface Window { google?: GoogleIdentity }
}

/** Si merece la pena intentarlo: sin clave, no. */
export function googleIdentityConfigured(): boolean {
  return CLIENT_ID.length > 0
}

let scriptPromise: Promise<boolean> | null = null

/** Carga el guion de Google una sola vez. `false` si no llega (red, bloqueador). */
function loadGoogleScript(): Promise<boolean> {
  if (window.google?.accounts?.id) return Promise.resolve(true)
  scriptPromise ??= new Promise<boolean>((resolve) => {
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${SCRIPT_URL}"]`)
    const script = existing ?? document.createElement('script')
    const done = () => resolve(Boolean(window.google?.accounts?.id))
    script.addEventListener('load', done, { once: true })
    script.addEventListener('error', () => resolve(false), { once: true })
    if (!existing) {
      script.src = SCRIPT_URL
      script.async = true
      document.head.appendChild(script)
    }
    /* Un tope: un guion que ni carga ni falla dejaría el botón girando. */
    window.setTimeout(done, 8_000)
  })
  return scriptPromise
}

function base64Url(bytes: Uint8Array): string {
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

/** El nonce: el original para Supabase y su SHA-256 para Google. */
async function makeNonce(): Promise<{ raw: string; hashed: string }> {
  const raw = base64Url(crypto.getRandomValues(new Uint8Array(32)))
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(raw))
  return { raw, hashed: base64Url(new Uint8Array(digest)) }
}

export type GoogleIdentityOutcome = 'signed-in' | 'unavailable' | 'error'

/**
 * Pide la cuenta a Google y abre la sesión en Supabase con su token.
 *
 * `signed-in` — dentro; la sesión ya está abierta y `onAuthStateChange` sigue.
 * `unavailable` — no se pudo preguntar (sin clave, sin guion, ventana cerrada).
 * `error` — Google contestó pero Supabase no aceptó el token.
 */
export async function signInWithGoogleIdentity(): Promise<GoogleIdentityOutcome> {
  if (!googleIdentityConfigured()) return 'unavailable'
  if (!(await loadGoogleScript())) return 'unavailable'
  const identity = window.google?.accounts?.id
  if (!identity) return 'unavailable'

  const { raw, hashed } = await makeNonce()
  const credential = await new Promise<string | null>((resolve) => {
    let settled = false
    const finish = (value: string | null) => {
      if (settled) return
      settled = true
      window.clearTimeout(timer)
      resolve(value)
    }
    const timer = window.setTimeout(() => {
      identity.cancel()
      finish(null)
    }, PROMPT_TIMEOUT_MS)

    try {
      identity.initialize({
        client_id: CLIENT_ID,
        callback: (response: CredentialResponse) => finish(response.credential ?? null),
        nonce: hashed,
        /* Que elija cuenta siempre, como hacía el `select_account` del camino
           con redirección: quien acaba de salir suele querer entrar con otra. */
        auto_select: false,
        cancel_on_tap_outside: true,
        /* La ventana del navegador (FedCM) y no la burbuja con cookies de
           terceros, que Chrome y Safari ya bloquean. */
        use_fedcm_for_prompt: true,
      })
      /* Sin cuenta que ofrecer —o con la ventana cerrada— avisa por aquí y se
         cae al otro camino en vez de dejar la pantalla esperando. */
      identity.prompt((notification) => {
        if (notification.isNotDisplayed?.() || notification.isSkippedMoment?.()) finish(null)
      })
    } catch {
      finish(null)
    }
  })

  if (!credential) return 'unavailable'

  const { error } = await supabase.auth.signInWithIdToken({
    provider: 'google',
    token: credential,
    nonce: raw,
  })
  if (error) {
    console.error('[auth] Supabase rechazó el token de Google', error)
    return 'error'
  }
  /* Para que la próxima vez vuelva a preguntar con qué cuenta. */
  identity.disableAutoSelect()
  return 'signed-in'
}
